import { defineStore } from 'pinia';
import StorageHandler from '@/utils/StorageHandler.js';
import FetchData from "@/utils/FetchData.js";
import CookieService from '@/utils/cookieService';

export const useUserStore = defineStore('userStore', {
  state: () => ({
    user: null,
    justLoggedIn: false,
    isValidatingToken: false, // Track validation state
  }),
  actions: {
    async loadUser() {
      // Try to load from sessionStorage first (priority for current session)
      let user = StorageHandler.LoadSessionStorage('user');
      
      this.justLoggedIn = false;
      
      // If not in session, try to restore from cookie token
      if (!user) {
        const cookieToken = CookieService.get('auth_token');
        if (cookieToken) {
          // Mark that we're validating
          this.isValidatingToken = true;
          
          // Create a temporary user object with just the token for FetchData
          const tempUser = { token: cookieToken };
          StorageHandler.InsertSessionStorage('user', tempUser);
          this.user = tempUser;
          
          // Validate token and get full user data
          await this.validateAndRestoreUser(cookieToken);
          
          this.isValidatingToken = false;
        }
      } else {
        this.user = user;
      }
    },
    
    setUser(userData) {
      this.user = userData;
      this.justLoggedIn = true;
      
      // Store full user data in sessionStorage (used by FetchData)
      StorageHandler.InsertSessionStorage('user', userData);
      
      // Store ONLY the token in cookie (base64 encoded and obfuscated)
      // This allows session restoration without exposing sensitive data
      if (userData.token) {
        CookieService.set('auth_token', userData.token, {
          expires: 30, // 30 days
          secure: process.env.NODE_ENV === 'production',
          sameSite: 'strict'
        });
      }

      // Language preference is stored in sessionStorage via languageStore
    },
    
    async validateAndRestoreUser(token) {
      try {
        const userData = await FetchData({}, "ValidateToken", "POST");
        
        if (userData && userData.id) {
          // Token is valid, update user data
          this.user = { ...userData, token };
          StorageHandler.InsertSessionStorage('user', { ...userData, token });
          
          // Refresh the token cookie expiration (no need to store user data)
          CookieService.set('auth_token', token, {
            expires: 30,
            secure: process.env.NODE_ENV === 'production',
            sameSite: 'strict'
          });
        } else {
          // Token validation failed
          this.logout();
        }
      } catch (error) {
        console.error('Failed to validate token:', error);
        this.logout();
      }
    },
    
    logout() {
      try {
        FetchData({}, "Logout", "POST");
      } catch (error) {
        console.error('Logout API call failed:', error);
      }
      
      this.user = null;
      this.justLoggedIn = false;
      this.isValidatingToken = false;

      StorageHandler.RemoveSessionStorage('user');
      StorageHandler.RemoveSessionStorageAll(["selectedLanguage", "languages"]);
      
      // Remove the token cookie
      CookieService.remove('auth_token');
    },
    
    isLoggedIn() {
      return !!this.user && !!this.user.token;
    },
    
    isAdmin(){
      return this.getRole() === 1;
    },
    
    getRole(){
      if(!this.user) return null;
      return this.user.role?.id || this.user.role;
    },
    
    hasPermission(permission) {
      if (!this.user) {
        return false;
      }
      
      if(this.isAdmin()) {
        return true;
      }
      
      if (typeof this.user[permission] === 'undefined') {
        return false;
      }
      
      return this.user[permission] === 'true' || this.user[permission] === true;
    }
  },
});